import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides, featuredArticles, latestUpdates, categoryHighlights, stats } from '@/mocks/home';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  useEffect(() => {
    const timer = setInterval(() => {
      goToSlide((currentSlide + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [currentSlide, goToSlide]);

  const hero = heroSlides[currentSlide];

  return (
    <>
      <SchemaOrg type="WebSite" data={{}} />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '日韩在线 - 正版日韩内容一站聚合平台',
          description: '日韩在线为您提供最新、最全的日韩影视、音乐、综艺、资讯内容，无需翻墙，极速流畅播放。',
          url: 'https://yongtaic.cn',
        }}
      />

      {/* Hero Section */}
      <section className="relative h-[520px] md:h-[680px] w-full overflow-hidden" aria-label="首页横幅">
        {heroSlides.map((slide, idx) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              idx === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50 z-20"></div>

        <div className="absolute inset-0 z-30 flex items-center">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <div
                key={hero.id}
                className="transition-all duration-500 ease-out"
                style={{
                  opacity: isTransitioning ? 0 : 1,
                  transform: isTransitioning ? 'translateY(20px)' : 'translateY(0)',
                }}
              >
                <p className="text-background-50/80 text-sm md:text-base font-label tracking-wider mb-3">
                  {hero.subtitle}
                </p>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-4 leading-tight">
                  {hero.title}
                </h1>
                <p className="text-background-50/70 text-base md:text-lg mb-8 max-w-2xl mx-auto">
                  {hero.description}
                </p>
                <Link
                  to={hero.ctaLink}
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-base font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  {hero.ctaText}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="flex items-center justify-center gap-2 mt-10">
                {heroSlides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => goToSlide(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                      idx === currentSlide ? 'bg-background-50 w-8' : 'bg-background-50/40 hover:bg-background-50/70'
                    }`}
                    aria-label={`切换到第${idx + 1}张横幅`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Highlights */}
      <section className="py-14 md:py-20 bg-background-50" aria-label="内容分类">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              探索日韩精彩内容
            </h2>
            <p className="text-foreground-600 text-sm md:text-base">
              影视、音乐、综艺、资讯 — 你想要的日韩内容，这里全都有
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {categoryHighlights.map((cat) => (
              <Link
                key={cat.id}
                to={cat.link}
                className="group relative bg-background-100 rounded-xl p-5 md:p-7 hover:bg-background-200 transition-all duration-300 cursor-pointer overflow-hidden"
              >
                <div className={`w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl ${cat.color} bg-opacity-90 mb-4 transition-transform duration-300 group-hover:scale-110`}>
                  <i className={`${cat.icon} text-background-50 text-xl md:text-2xl`}></i>
                </div>
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-1.5">
                  {cat.name}
                </h3>
                <p className="text-sm text-foreground-600 mb-3">{cat.description}</p>
                <span className="text-xs font-label text-foreground-500 bg-background-200/80 px-2.5 py-1 rounded-full">
                  {cat.count} 部内容
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-14 md:py-20 bg-background-100" aria-label="精选推荐">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                精选推荐
              </h2>
              <p className="text-sm text-foreground-600">编辑团队为你精选的优质内容</p>
            </div>
            <Link
              to="/category"
              className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {featuredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:bg-background-200/50"
              >
                <div className="relative overflow-hidden aspect-[16/9]">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    title={`${article.title} - 日韩在线`}
                  />
                  <span className="absolute top-3 left-3 px-2.5 py-1 bg-primary-500/90 text-background-50 text-xs font-medium rounded-full">
                    {article.category}
                  </span>
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="text-base md:text-lg font-heading font-bold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span>{article.author}</span>
                    <span>·</span>
                    <span>{article.date}</span>
                    <span>·</span>
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Link
              to="/category"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-16 bg-primary-500" aria-label="平台数据">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-1.5">
                  {stat.value}
                </div>
                <div className="text-sm text-background-50/70 font-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="py-14 md:py-20 bg-background-50" aria-label="最新更新">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                最新更新
              </h2>
              <p className="text-sm text-foreground-600">实时同步日韩最新内容动态</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {latestUpdates.map((item) => (
              <Link
                key={item.id}
                to={`/article/${item.id}`}
                className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:bg-background-200"
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    title={`${item.title} - 日韩在线`}
                  />
                  <span className="absolute top-2 left-2 px-2 py-0.5 bg-background-950/70 text-background-50 text-[10px] font-medium rounded-full">
                    {item.category}
                  </span>
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1.5">{item.date}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-14 md:py-20 bg-background-100" aria-label="订阅">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-accent-100">
            <i className="ri-mail-send-line text-accent-600 text-2xl"></i>
          </div>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            订阅日韩在线资讯
          </h2>
          <p className="text-sm text-foreground-600 mb-8 max-w-md mx-auto">
            第一时间获取最新日韩影视、音乐、综艺和娱乐资讯，精彩内容不再错过
          </p>
          <form
            className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto"
            data-readdy-form="true"
            action="https://readdy.ai/api/form/d9srgellbrt1qcgaj8q0"
            method="POST"
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
              if (honeypot) return;
              const formData = new FormData(form);
              const body = new URLSearchParams();
              formData.forEach((v, k) => { body.append(k, v as string); });
              try {
                const res = await fetch(form.action, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
                const text = await res.text();
                let msg = '';
                try { const j = JSON.parse(text); msg = j?.meta?.message || j?.message || ''; } catch { /* ignore parse errors */ }
                if (res.ok && msg !== 'spam' && !msg.includes('spam')) {
                  alert('订阅成功！感谢您的关注。');
                  form.reset();
                } else {
                  alert(msg || '订阅失败，请稍后再试。');
                }
              } catch {
                alert('网络错误，请稍后再试。');
              }
            }}
          >
            <input
              type="email"
              name="email"
              placeholder="请输入您的邮箱地址"
              required
              className="flex-1 w-full px-5 py-3 rounded-full bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
            />
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              readOnly
              aria-hidden="true"
              className="antispam-field"
            />
            <button
              type="submit"
              className="w-full sm:w-auto px-7 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              立即订阅
            </button>
          </form>
        </div>
      </section>
    </>
  );
}